import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function FinancialReports() {
  const [report, setReport] = useState([]);
  const [summary, setSummary] = useState({ totalRevenue: 0, bookingsCount: 0 });
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const res = await api.get('/admin/financial-reports');
      const data = res.data || { summary: { totalRevenue: 0, bookingsCount: 0 }, byEvent: [], trend: [] };
      setReport(data.byEvent || []);
      setSummary({ totalRevenue: Number(data.summary?.totalRevenue || 0), bookingsCount: Number(data.summary?.bookingsCount || 0) });
      setTrend(data.trend || []);
    } catch (err) {
      console.error('Failed to fetch financial reports:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">
          💰 Financial Reports
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-600 text-white p-5 rounded-xl shadow-lg hover:shadow-2xl transition">
            <h3 className="text-lg font-semibold">Total Revenue</h3>
            <p className="text-3xl font-bold mt-2">
              ₹{summary.totalRevenue.toLocaleString()}
            </p>
          </div>

          <div className="bg-green-600 text-white p-5 rounded-xl shadow-lg hover:shadow-2xl transition">
            <h3 className="text-lg font-semibold">Total Bookings</h3>
            <p className="text-3xl font-bold mt-2">{summary.bookingsCount}</p>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">
            📈 Revenue Trend (Last 30 Days)
          </h3>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500">No trend data available.</p>
          )}
        </div>

        {/* Event-wise Financial Table */}
        {loading ? (
          <p className="text-gray-500">Loading reports...</p>
        ) : report.length === 0 ? (
          <p className="text-gray-500">No reports available.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl shadow-md">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700 uppercase text-sm">
                  <th className="p-3 text-left">Event Name</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Revenue (₹)</th>
                  <th className="p-3 text-left">Seats Sold</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b hover:bg-blue-50 transition"
                  >
                    <td className="p-3 font-medium text-gray-800">{r.name}</td>
                    <td className="p-3 text-gray-600">
                      {new Date(r.date).toLocaleDateString()}
                    </td>
                    <td className="p-3 font-semibold text-green-700">
                      ₹{(r.revenue ?? 0).toLocaleString()}
                    </td>
                    <td className="p-3">{r.seats_sold ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
